'use client';

import NextImage from 'next/image';
import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Mail, Loader2, Send, Hash, Link2, Paperclip, Users } from 'lucide-react';
import EmailRecipientsInput from '@/app/client-zone/modules/total-connect/components/EmailRecipientsInput';
import { useDocumentShares } from '@/app/client-zone/modules/total-finance/hooks/useDocumentShares';
import { useDocuments } from '@/app/client-zone/modules/total-finance/hooks/useDocuments';
import { useContacts } from '@/app/client-zone/modules/total-finance/hooks/useContacts';
import { useTheme } from '@/contexts/ThemeContext';
import toast from 'react-hot-toast';
import { authService } from '@/lib/authService';
import { getFinanceDocumentDefinition, type CanonicalFinanceItemType } from '@/lib/finance/financeDocumentTypes';
import { DOCUMENT_TYPE_LABELS, type DocumentType } from '@/lib/reports/types';
import { cn } from '@/lib/utils';
import { emitGuidedTourTrainingEvent } from '@/components/training/guided-tour';

interface SendDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  preSelectedDocumentId?: number | string;
  preSelectedDocumentType?: string;
  preSelectedDocument?: any;
}

type SendMethod = 'secure-link' | 'pdf';

interface EmailSuggestion {
  email: string;
  name?: string;
  source: 'recent' | 'contact' | 'company' | 'crm' | 'personal';
  avatar_url?: string;
  job_title?: string;
  contact_type?: string;
  company?: string;
}

const RECENT_RECIPIENTS_KEY = 'recentEmailRecipients';
const COMPANY_USERS_KEY = 'companyEmailUsers';
const PAYMENT_REMINDER_DAY_OPTIONS = [1, 3, 7, 14, 21, 30];

function readStoredArray<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function resolveSelectedDocumentType(
  explicitType?: string | null,
  document?: any,
): CanonicalFinanceItemType {
  const candidates = [
    explicitType,
    document?.doc_type,
    document?.item_type,
    document?.document_type,
  ];

  for (const candidate of candidates) {
    if (candidate && getFinanceDocumentDefinition(candidate)) {
      return candidate as CanonicalFinanceItemType;
    }
  }

  return 'invoice';
}

function extractEmails(value: string): string[] {
  if (!value?.trim()) return [];
  const parts: string[] = [];
  let current = '';
  let inBracket = false;
  for (const char of value) {
    if (char === '<') inBracket = true;
    if (char === '>') inBracket = false;
    if (char === ',' && !inBracket) {
      if (current.trim()) parts.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  if (current.trim()) parts.push(current.trim());

  return parts.map((part) => {
    const match = part.match(/<([^<>]+@[^<>]+)>/);
    return match ? match[1].trim() : part.trim();
  }).filter((e) => e.includes('@'));
}

function getDocumentLabel(docType?: string): string {
  if (!docType) return 'Document';
  return DOCUMENT_TYPE_LABELS[docType as DocumentType] || docType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function SendDocumentModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  preSelectedDocumentId,
  preSelectedDocumentType,
  preSelectedDocument
}: SendDocumentModalProps) {
  const { createShare } = useDocumentShares();
  const { documents: invoices, loadDocuments: loadInvoices } = useDocuments('invoice');
  const { contacts, loadContacts } = useContacts();
  const { clientBranding, currentTheme } = useTheme();
  const resolvedDocumentType = resolveSelectedDocumentType(preSelectedDocumentType, preSelectedDocument);
  const docLabel = getDocumentLabel(resolvedDocumentType);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSending, setIsSending] = useState(false);
  const [sendMethod, setSendMethod] = useState<SendMethod>('pdf');
  const [showCcBcc, setShowCcBcc] = useState(false);

  const [toValue, setToValue] = useState('');
  const [ccValue, setCcValue] = useState('');
  const [bccValue, setBccValue] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [documentId, setDocumentId] = useState<number | string>(
    preSelectedDocumentId || preSelectedDocument?.id || '',
  );

  const [suggestions, setSuggestions] = useState<EmailSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [activeField, setActiveField] = useState<'to' | 'cc' | 'bcc'>('to');
  const [enablePaymentFollowUps, setEnablePaymentFollowUps] = useState(false);
  const [selectedReminderDays, setSelectedReminderDays] = useState<number[]>([]);

  const toInputRef = useRef<HTMLInputElement>(null);
  const ccInputRef = useRef<HTMLInputElement>(null);
  const bccInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const hasPreSelectedDocument = Boolean(preSelectedDocumentId || preSelectedDocument?.id);
  const supportsPaymentReminders = resolvedDocumentType === 'invoice';

  // Sync document_id from props
  useEffect(() => {
    const docId = preSelectedDocumentId || preSelectedDocument?.id;
    if (docId) setDocumentId(docId);
  }, [preSelectedDocumentId, preSelectedDocument?.id, isOpen]);

  // Load contacts for document selector fallback
  useEffect(() => {
    loadContacts();
    if (!hasPreSelectedDocument) loadInvoices();
  }, [hasPreSelectedDocument, loadContacts, loadInvoices]);

  // Pre-populate recipient and subject from document
  useEffect(() => {
    if (!isOpen) return;

    // Pre-fill subject
    const docNum = preSelectedDocument?.doc_number || '';
    setSubject(`${docLabel}${docNum ? ` ${docNum}` : ''}`);

    if (preSelectedDocument) {
      if (preSelectedDocument.contact_email) {
        const name = preSelectedDocument.contact_name || '';
        setToValue(name ? `${name} <${preSelectedDocument.contact_email}>` : preSelectedDocument.contact_email);
      } else if (preSelectedDocument.contact_id && contacts.length > 0) {
        const contact = contacts.find(
          (c) => c.id === preSelectedDocument.contact_id || c.id === Number(preSelectedDocument.contact_id),
        );
        if (contact?.email) {
          const name = contact.contact_person || contact.company_name || preSelectedDocument.contact_name || '';
          setToValue(name ? `${name} <${contact.email}>` : contact.email);
        }
      }
    }
  }, [preSelectedDocument, contacts, isOpen, docLabel]);

  // Suggestion loading (mirrors MultiComposeManager approach)
  const loadSuggestions = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const recent = readStoredArray<EmailSuggestion>(RECENT_RECIPIENTS_KEY);
    const company = readStoredArray<EmailSuggestion>(COMPANY_USERS_KEY);
    const cached = [...recent, ...company];
    const q = query.toLowerCase();
    const filtered = cached.filter(
      (s) => s.email.toLowerCase().includes(q) || s.name?.toLowerCase().includes(q),
    );
    const unique = filtered.filter(
      (s, i, arr) => arr.findIndex((x) => x.email.toLowerCase() === s.email.toLowerCase()) === i,
    );

    setSuggestions(unique.slice(0, 8));
    setShowSuggestions(unique.length > 0);
    setSelectedSuggestionIndex(-1);

    // Fetch from server for comprehensive results
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      const response = await fetch('/api/v1/email-contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({ action: 'searchAllContacts', query }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.length > 0) {
          const serverResults: EmailSuggestion[] = data.data.map((c: any) => ({
            email: c.email,
            name: c.name,
            source: c.source as EmailSuggestion['source'],
            avatar_url: c.avatar_url || c.photo_url,
            job_title: c.job_title,
            contact_type: c.contact_type,
            company: c.company,
          }));
          const all = [...unique, ...serverResults];
          const deduped = all.filter(
            (s, i, arr) => arr.findIndex((x) => x.email.toLowerCase() === s.email.toLowerCase()) === i,
          );
          setSuggestions(deduped.slice(0, 10));
          setShowSuggestions(deduped.length > 0);
        }
      }
    } catch {
      // Use cached only
    }
  }, []);

  const selectSuggestion = useCallback(
    (suggestion: EmailSuggestion) => {
      const setter = activeField === 'cc' ? setCcValue : activeField === 'bcc' ? setBccValue : setToValue;
      const current = activeField === 'cc' ? ccValue : activeField === 'bcc' ? bccValue : toValue;

      const parts = current.split(',').map((p) => p.trim());
      parts[parts.length - 1] = suggestion.name
        ? `${suggestion.name} <${suggestion.email}>`
        : suggestion.email;

      setter(parts.join(', ') + ', ');

      // Save to recent
      try {
        const recent = readStoredArray<EmailSuggestion>(RECENT_RECIPIENTS_KEY);
        const idx = recent.findIndex((r) => r.email.toLowerCase() === suggestion.email.toLowerCase());
        if (idx >= 0) recent.splice(idx, 1);
        recent.unshift(suggestion);
        localStorage.setItem(RECENT_RECIPIENTS_KEY, JSON.stringify(recent.slice(0, 50)));
      } catch { /* ignore */ }

      setShowSuggestions(false);
      setSuggestions([]);
      setSelectedSuggestionIndex(-1);
    },
    [activeField, toValue, ccValue, bccValue],
  );

  const handleFieldKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showSuggestions || suggestions.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIndex((i) => Math.min(i + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[selectedSuggestionIndex]);
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    },
    [showSuggestions, suggestions, selectedSuggestionIndex, selectSuggestion],
  );

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};
    const toEmails = extractEmails(toValue);

    if (toEmails.length === 0) {
      newErrors.to = 'At least one recipient is required';
    }

    if (!documentId) {
      newErrors.document_id = 'Please select a document';
    }

    if (supportsPaymentReminders && enablePaymentFollowUps && selectedReminderDays.length === 0) {
      newErrors.payment_reminders = 'Select at least one follow-up interval';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [toValue, documentId, supportsPaymentReminders, enablePaymentFollowUps, selectedReminderDays.length]);

  const toggleReminderDay = useCallback((day: number) => {
    setSelectedReminderDays((current) => (
      current.includes(day)
        ? current.filter((value) => value !== day)
        : [...current, day].sort((a, b) => a - b)
    ));
  }, []);

  const schedulePaymentReminders = useCallback(async (args: {
    docId: number | string;
    toEmails: string[];
    ccEmails: string[];
    bccEmails: string[];
  }) => {
    const tokens = authService.getTokens();
    if (!tokens?.access_token) {
      throw new Error('No authentication token');
    }

    const response = await fetch('/api/v1/finance/payment-reminders', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokens.access_token}`,
      },
      body: JSON.stringify({
        invoice_id: String(args.docId),
        recipients: args.toEmails,
        cc: args.ccEmails,
        bcc: args.bccEmails,
        days_after_send: selectedReminderDays,
        sent_at: new Date().toISOString(),
      }),
    });

    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to schedule payment reminders');
    }

    return Array.isArray(result.data) ? result.data.length : 0;
  }, [selectedReminderDays]);

  const handleSendSecureLink = async () => {
    if (!validateForm()) return;

    const docId = documentId || preSelectedDocumentId || preSelectedDocument?.id;
    if (!docId) {
      toast.error('No document selected.');
      return;
    }

    const toEmails = extractEmails(toValue);
    setIsSending(true);
    const toastId = toast.loading('Sending secure link...');

    try {
      const shareData = {
        document_id: typeof docId === 'string' && String(docId).includes('-') ? docId : Number(docId),
        document_type: resolveSelectedDocumentType(resolvedDocumentType, preSelectedDocument),
        recipient_email: toEmails[0],
        recipient_name: undefined as string | undefined,
        custom_message: message || undefined,
      };

      const result = await createShare(shareData);

      if (result?.id && result?.token) {
        toast.success(`Secure link sent to ${toEmails[0]}`, { id: toastId, duration: 4000 });
        onSuccess?.();
        resetAndClose();
      } else {
        toast.error('Failed to send secure link.', { id: toastId, duration: 5000 });
        setIsSending(false);
      }
    } catch (error) {
      console.error('Error creating share:', error);
      toast.error('Failed to send secure link.', { id: toastId, duration: 5000 });
      setIsSending(false);
    }
  };

  const handleSendPdf = async () => {
    if (!validateForm()) return;

    const docId = documentId || preSelectedDocumentId || preSelectedDocument?.id;
    if (!docId) {
      toast.error('No document selected.');
      return;
    }

    const toEmails = extractEmails(toValue);
    const ccEmails = extractEmails(ccValue);
    const bccEmails = extractEmails(bccValue);

    setIsSending(true);
    const toastId = toast.loading('Generating and sending PDF...');

    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) throw new Error('No authentication token');

      const response = await fetch('/api/v1/finance/send-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          document_id: typeof docId === 'string' && String(docId).includes('-') ? docId : Number(docId),
          document_type: resolveSelectedDocumentType(resolvedDocumentType, preSelectedDocument),
          recipient_email: toEmails.join(', '),
          cc: ccEmails.length > 0 ? ccEmails : undefined,
          bcc: bccEmails.length > 0 ? bccEmails : undefined,
          subject: subject.trim() || undefined,
          custom_message: message.trim() || undefined,
        }),
      });

      const result = await response.json();

      if (result.success) {
        let scheduledReminderCount = 0;
        let reminderError: string | null = null;

        if (supportsPaymentReminders && enablePaymentFollowUps && selectedReminderDays.length > 0) {
          try {
            scheduledReminderCount = await schedulePaymentReminders({
              docId,
              toEmails,
              ccEmails,
              bccEmails,
            });
          } catch (error) {
            console.error('Error scheduling payment reminders:', error);
            reminderError = error instanceof Error ? error.message : 'Failed to schedule payment reminders';
          }
        }

        const count = toEmails.length;
        if (reminderError) {
          toast.error(
            `PDF sent to ${count} recipient${count === 1 ? '' : 's'}, but payment reminders were not scheduled: ${reminderError}`,
            { id: toastId, duration: 7000 },
          );
        } else {
          const reminderSuffix = scheduledReminderCount > 0
            ? ` and ${scheduledReminderCount} payment reminder${scheduledReminderCount === 1 ? '' : 's'} scheduled`
            : '';
          toast.success(`PDF sent to ${count} recipient${count === 1 ? '' : 's'}${reminderSuffix}`, { id: toastId, duration: 5000 });
        }
        emitGuidedTourTrainingEvent({ type: 'document_send' });
        onSuccess?.();
        resetAndClose();
      } else {
        toast.error(result.error || 'Failed to send PDF.', { id: toastId, duration: 5000 });
        setIsSending(false);
      }
    } catch (error) {
      console.error('Error sending PDF:', error);
      toast.error('Failed to send PDF.', { id: toastId, duration: 5000 });
      setIsSending(false);
    }
  };

  const handleSend = () => {
    if (sendMethod === 'secure-link') {
      handleSendSecureLink();
    } else {
      handleSendPdf();
    }
  };

  function resetAndClose() {
    setToValue('');
    setCcValue('');
    setBccValue('');
    setSubject('');
    setMessage('');
    setEnablePaymentFollowUps(false);
    setSelectedReminderDays([]);
    setErrors({});
    setIsSending(false);
    setShowCcBcc(false);
    setSuggestions([]);
    setShowSuggestions(false);
    setTimeout(() => onClose(), 300);
  }

  function handleClose() {
    setToValue('');
    setCcValue('');
    setBccValue('');
    setMessage('');
    setEnablePaymentFollowUps(false);
    setSelectedReminderDays([]);
    setErrors({});
    setShowCcBcc(false);
    setSuggestions([]);
    setShowSuggestions(false);
    onClose();
  }

  if (!isOpen) return null;

  const isDarkMode = currentTheme?.mode === 'dark';
  const primaryColor = clientBranding?.primary_color || '#1e3a5f';

  const renderSuggestionsDropdown = (field: 'to' | 'cc' | 'bcc') => {
    if (!showSuggestions || activeField !== field || suggestions.length === 0) return null;
    return (
      <div
        ref={suggestionsRef}
        className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto"
      >
        {suggestions.map((s, i) => (
          <button
            key={`${s.email}-${i}`}
            type="button"
            onClick={() => selectSuggestion(s)}
            className={cn(
              'flex items-center gap-3 w-full px-3 py-2 text-left text-sm',
              selectedSuggestionIndex === i
                ? 'bg-blue-50 dark:bg-blue-900/30'
                : 'hover:bg-gray-50 dark:hover:bg-gray-800',
            )}
          >
            <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden flex-shrink-0">
              {s.avatar_url ? (
                <NextImage src={s.avatar_url} alt="" width={28} height={28} className="w-full h-full object-cover" unoptimized />
              ) : (
                <Users className="w-3.5 h-3.5 text-gray-500" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              {s.name && (
                <div className="text-gray-900 dark:text-gray-100 truncate text-xs font-medium">{s.name}</div>
              )}
              <div className="text-gray-500 dark:text-gray-400 truncate text-xs">{s.email}</div>
            </div>
            {s.contact_type && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 capitalize flex-shrink-0">
                {s.contact_type}
              </span>
            )}
            {s.source === 'company' && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex-shrink-0">
                Team
              </span>
            )}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-2 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className={cn(
          'rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col',
          isDarkMode ? 'bg-gray-900' : 'bg-white',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${adjustHex(primaryColor, -30)} 100%)` }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-white truncate">Send {docLabel}</h2>
                <p className="text-white/60 text-sm flex items-center gap-2 mt-0.5">
                  {preSelectedDocument?.doc_number && (
                    <>
                      <Hash className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{preSelectedDocument.doc_number}</span>
                    </>
                  )}
                  {preSelectedDocument?.contact_name && (
                    <span className="truncate">— {preSelectedDocument.contact_name}</span>
                  )}
                  {preSelectedDocument?.grand_total != null && (
                    <span className="flex-shrink-0">R{Number(preSelectedDocument.grand_total).toFixed(2)}</span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/10 rounded-xl transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Error banner */}
        {errors.general && (
          <div className={cn('mx-4 sm:mx-6 mt-3 p-3 rounded-lg border text-sm', isDarkMode ? 'bg-red-900/30 border-red-700 text-red-400' : 'bg-red-50 border-red-200 text-red-600')}>
            {errors.general}
          </div>
        )}

        {/* Form body */}
        <div className="flex-1 overflow-y-auto">
          {/* Send Method Toggle */}
          <div className="px-4 sm:px-6 pt-4 pb-2">
            <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button
                type="button"
                onClick={() => setSendMethod('pdf')}
                className={cn(
                  'flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium transition-colors',
                  sendMethod === 'pdf'
                    ? 'bg-green-600 text-white'
                    : isDarkMode ? 'bg-gray-800 text-gray-400 hover:text-gray-200' : 'bg-gray-50 text-gray-600 hover:text-gray-800',
                )}
              >
                <Paperclip className="w-4 h-4" />
                PDF Attachment
              </button>
              <button
                type="button"
                onClick={() => setSendMethod('secure-link')}
                className={cn(
                  'flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium transition-colors',
                  sendMethod === 'secure-link'
                    ? 'bg-blue-600 text-white'
                    : isDarkMode ? 'bg-gray-800 text-gray-400 hover:text-gray-200' : 'bg-gray-50 text-gray-600 hover:text-gray-800',
                )}
              >
                <Link2 className="w-4 h-4" />
                Secure Link
              </button>
            </div>
          </div>

          {/* Email-style fields */}
          <div className="px-4 sm:px-6 space-y-0 divide-y divide-gray-100 dark:divide-gray-800">
            {/* Document selector (when no preselected) */}
            {!hasPreSelectedDocument && (
              <div className="py-3">
                <label className={cn('block text-sm font-medium mb-1.5', isDarkMode ? 'text-gray-300' : 'text-gray-700')}>
                  Select Document
                </label>
                <select
                  value={documentId}
                  onChange={(e) => setDocumentId(Number(e.target.value))}
                  className={cn(
                    'w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    errors.document_id ? 'border-red-500' : isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300',
                  )}
                >
                  <option value="">Select a document...</option>
                  {invoices.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      {inv.doc_number} - {inv.contact_name} - R{Number(inv.grand_total || 0).toFixed(2)}
                    </option>
                  ))}
                </select>
                {errors.document_id && <p className="mt-1 text-xs text-red-500">{errors.document_id}</p>}
              </div>
            )}

            {/* To field */}
            <div className="relative py-2.5 flex items-start gap-2">
              <span className={cn('text-sm pt-1 w-10 flex-shrink-0', isDarkMode ? 'text-gray-400' : 'text-gray-500')}>To:</span>
              <div className="flex-1 min-w-0">
                <EmailRecipientsInput
                  value={toValue}
                  onChange={setToValue}
                  onFocus={() => setActiveField('to')}
                  placeholder="Type name or email..."
                  suggestions={suggestions}
                  showSuggestions={showSuggestions && activeField === 'to'}
                  selectedSuggestionIndex={selectedSuggestionIndex}
                  onLoadSuggestions={loadSuggestions}
                  onSelectSuggestion={selectSuggestion}
                  onKeyDown={handleFieldKeyDown}
                  inputRef={toInputRef}
                />
                {renderSuggestionsDropdown('to')}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0 pt-0.5">
                {!showCcBcc && (
                  <button
                    type="button"
                    onClick={() => setShowCcBcc(true)}
                    className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-1.5 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    Cc/Bcc
                  </button>
                )}
              </div>
              {errors.to && <p className="absolute -bottom-0.5 left-12 text-xs text-red-500">{errors.to}</p>}
            </div>

            {/* CC field */}
            {showCcBcc && (
              <div className="relative py-2.5 flex items-start gap-2">
                <span className={cn('text-sm pt-1 w-10 flex-shrink-0', isDarkMode ? 'text-gray-400' : 'text-gray-500')}>Cc:</span>
                <div className="flex-1 min-w-0">
                  <EmailRecipientsInput
                    value={ccValue}
                    onChange={setCcValue}
                    onFocus={() => setActiveField('cc')}
                    placeholder="Add Cc recipients..."
                    suggestions={suggestions}
                    showSuggestions={showSuggestions && activeField === 'cc'}
                    selectedSuggestionIndex={selectedSuggestionIndex}
                    onLoadSuggestions={loadSuggestions}
                    onSelectSuggestion={selectSuggestion}
                    onKeyDown={handleFieldKeyDown}
                    inputRef={ccInputRef}
                  />
                  {renderSuggestionsDropdown('cc')}
                </div>
              </div>
            )}

            {/* BCC field */}
            {showCcBcc && (
              <div className="relative py-2.5 flex items-start gap-2">
                <span className={cn('text-sm pt-1 w-10 flex-shrink-0', isDarkMode ? 'text-gray-400' : 'text-gray-500')}>Bcc:</span>
                <div className="flex-1 min-w-0">
                  <EmailRecipientsInput
                    value={bccValue}
                    onChange={setBccValue}
                    onFocus={() => setActiveField('bcc')}
                    placeholder="Add Bcc recipients..."
                    suggestions={suggestions}
                    showSuggestions={showSuggestions && activeField === 'bcc'}
                    selectedSuggestionIndex={selectedSuggestionIndex}
                    onLoadSuggestions={loadSuggestions}
                    onSelectSuggestion={selectSuggestion}
                    onKeyDown={handleFieldKeyDown}
                    inputRef={bccInputRef}
                  />
                  {renderSuggestionsDropdown('bcc')}
                </div>
              </div>
            )}

            {/* Subject field */}
            {sendMethod === 'pdf' && (
              <div className="py-2.5 flex items-center gap-2">
                <span className={cn('text-sm w-10 flex-shrink-0', isDarkMode ? 'text-gray-400' : 'text-gray-500')}>Subj:</span>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Email subject line"
                  className={cn(
                    'flex-1 bg-transparent border-0 text-sm focus:ring-0 focus:outline-none p-0',
                    isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400',
                  )}
                />
              </div>
            )}
          </div>

          {/* Message */}
          <div className="px-4 sm:px-6 pt-3 pb-4">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a personal message (optional)..."
              rows={4}
              className={cn(
                'w-full px-3 py-2.5 text-sm rounded-lg border resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400',
              )}
            />
          </div>

          {supportsPaymentReminders && (
            <div className="px-4 sm:px-6 pb-4">
              <div className={cn(
                'rounded-xl border p-4',
                isDarkMode ? 'border-gray-700 bg-gray-800/70' : 'border-gray-200 bg-gray-50',
              )}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enablePaymentFollowUps}
                    onChange={(e) => setEnablePaymentFollowUps(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <div className={cn('text-sm font-medium', isDarkMode ? 'text-gray-100' : 'text-gray-900')}>
                      Schedule automatic payment follow-ups
                    </div>
                    <div className={cn('text-xs mt-1', isDarkMode ? 'text-gray-400' : 'text-gray-600')}>
                      Send the customer&apos;s latest statement automatically after the selected number of days. Reminder emails will be removed automatically once a payment is allocated to the invoice.
                    </div>
                  </div>
                </label>

                {enablePaymentFollowUps && (
                  <div className="mt-4">
                    <div className={cn('text-xs font-medium mb-2 uppercase tracking-wide', isDarkMode ? 'text-gray-400' : 'text-gray-500')}>
                      Reminder days after sending
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {PAYMENT_REMINDER_DAY_OPTIONS.map((day) => {
                        const selected = selectedReminderDays.includes(day);
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => toggleReminderDay(day)}
                            className={cn(
                              'px-3 py-2 rounded-lg text-sm font-medium border transition-colors',
                              selected
                                ? 'bg-blue-600 text-white border-blue-600'
                                : isDarkMode
                                  ? 'border-gray-600 text-gray-200 hover:bg-gray-700'
                                  : 'border-gray-300 text-gray-700 hover:bg-white',
                            )}
                          >
                            {day} day{day === 1 ? '' : 's'}
                          </button>
                        );
                      })}
                    </div>
                    {errors.payment_reminders && (
                      <p className="mt-2 text-xs text-red-500">{errors.payment_reminders}</p>
                    )}
                    <p className={cn('text-xs mt-3', isDarkMode ? 'text-gray-500' : 'text-gray-500')}>
                      The reminder statement email will go to the same To / Cc / Bcc recipients selected here.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Method info (collapsible) */}
          <div className="px-4 sm:px-6 pb-4">
            <div className={cn(
              'px-3 py-2.5 rounded-lg text-xs flex items-start gap-2',
              sendMethod === 'secure-link'
                ? isDarkMode ? 'bg-blue-900/20 text-blue-300' : 'bg-blue-50 text-blue-700'
                : isDarkMode ? 'bg-green-900/20 text-green-300' : 'bg-green-50 text-green-700',
            )}>
              {sendMethod === 'secure-link' ? (
                <>
                  <Link2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>Recipient receives a secure, trackable link. You can revoke access anytime. Only the first To recipient receives the link.</span>
                </>
              ) : (
                <>
                  <Paperclip className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>A professional PDF will be generated and sent as an email attachment to all recipients.</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={cn(
          'px-4 sm:px-6 py-3 border-t flex items-center justify-between flex-shrink-0',
          isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50',
        )}>
          <div className="text-xs text-gray-400 hidden sm:block">
            {extractEmails(toValue).length > 0 && (
              <span>{extractEmails(toValue).length} recipient{extractEmails(toValue).length !== 1 ? 's' : ''}</span>
            )}
            {extractEmails(ccValue).length > 0 && (
              <span>, {extractEmails(ccValue).length} Cc</span>
            )}
            {extractEmails(bccValue).length > 0 && (
              <span>, {extractEmails(bccValue).length} Bcc</span>
            )}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={handleClose}
              disabled={isSending}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100',
              )}
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={isSending || extractEmails(toValue).length === 0}
              className={cn(
                'px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all disabled:opacity-50',
                sendMethod === 'secure-link'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white',
              )}
            >
              {isSending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  {sendMethod === 'secure-link' ? 'Send Link' : 'Send PDF'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function adjustHex(hex: string, amount: number): string {
  const clean = hex.replace('#', '');
  const num = parseInt(clean, 16);
  const r = Math.max(0, Math.min(255, ((num >> 16) & 255) + amount));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 255) + amount));
  const b = Math.max(0, Math.min(255, (num & 255) + amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
